import { FunctionComponent, useCallback } from 'react'
import { getTheme, Text } from '@conexasaude/conexa-hero'
import Modal from '@/components/Modal'
import useStyles from './styles'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useNavigation } from '@/hooks/useNavigation'
import { useAuthContext } from '../../contexts/AuthContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useMinorAccessRequestStore } from '../../store/MinorAccessRequest.store'
import { IUser } from '../../auth-types'
import themeServices from '@/theme/theme.services'
import service from '@/modules/Auth/auth-services'
import { setRefreshToken, setToken } from '@/utils/token'
import { getCache, setCache } from '@/utils/cache'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'
import { useCheckMinorsTermAcceptance } from '../../queries'

const AccessRequestSentModal: FunctionComponent = () => {
  const styles = useStyles()
  const theme = getTheme()
  const { showModal, closeModal } = useModalContext()
  const navigate = useNavigation()
  const { signIn, choiceUser, setUserClinic } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const { minorAccessRequestData } = useMinorAccessRequestStore()
  const { mutateAsync: checkMinorsTermAcceptance } =
    useCheckMinorsTermAcceptance()

  const handlePrimaryAction = async () => {
    try {
      const token = minorAccessRequestData?.token
      const acceptedTerm = await checkMinorsTermAcceptance(token)
      if (acceptedTerm) setUser()
      else {
        closeModal()
        showModal('GUARDIAN_ACCESS_REQUEST_MODAL', {
          title: 'Aguardando liberação',
          persistent: true,
        })
      }
    } catch (error) {
      showErrorToast(error)
    }
  }

  const setUser = useCallback(async () => {
    try {
      const users = getCache<IUser[]>('users')
      const user = users?.find(
        user =>
          user?.usuario?.paciente?.id === minorAccessRequestData?.idPatient,
      )

      const { token, refreshToken } = user
      const { flagAcessou, clinica, id } = user?.usuario ?? {}

      const [userClinic, clinicColors, ip] = await Promise.all([
        service.getClinic(token),
        themeServices.getClinicColorsById(clinica.id),
        service.getUserIP().catch(() => {
          return ''
        }),
      ])

      const { clinic, flags } = userClinic

      if (!flagAcessou) await service.validateFirstAccess(token)

      signIn(users)
      choiceUser(id, ip)
      setToken(token)
      setRefreshToken(refreshToken)
      setCache('user', id)
      setThemeColors(clinicColors)
      setUserClinic(userClinic)
      setSpecialities(clinic)

      if (flags?.PermiteGeolocalizacao) {
        navigator.geolocation.getCurrentPosition(position => {
          const {
            accuracy,
            altitude,
            altitudeAccuracy,
            heading,
            latitude,
            longitude,
            speed,
          } = position.coords
          service.sendGeolocation({
            accuracy,
            altitude,
            altitudeAccuracy,
            heading,
            latitude,
            longitude,
            speed,
          })
        })
      }

      navigate('/')
    } catch (err) {
      showErrorToast(err)
    } finally {
      closeModal()
    }
  }, [
    signIn,
    choiceUser,
    setThemeColors,
    setUserClinic,
    setSpecialities,
    navigate,
    minorAccessRequestData?.idPatient,
    closeModal,
  ])
  return (
    <Modal
      id="ACCESS_REQUEST_SEND_MODAL"
      customHeader={
        <Text
          variant="headingSmall"
          fontWeight={600}
          color={theme.colors.brand.primary[300]}
        >
          Solicitação enviada
        </Text>
      }
      primaryActionLabel="Ok, entendi"
      primaryAction={handlePrimaryAction}
      size={480}
    >
      <div css={styles.modalContent}>
        <Text variant="paragraphLarge">
          Enviamos uma solicitação para os contatos que você preencheu, agora,
          aguarde que assim que aprovado o seu acesso, enviaremos um e-mail para
          você, ok?
        </Text>
      </div>
    </Modal>
  )
}

export { AccessRequestSentModal }
